import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, InputAdornment, List,
  ListItemButton, ListItemText, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import api from '../../../../services/api';

const ROTA = '/admin/snapshot';
const POR_PAGINA = 50;

/**
 * Escolha de equipamentos com busca e paginacao no SERVIDOR.
 *
 * A versao anterior pedia `limit: 1000` e desenhava tudo. Com um parque de dois
 * mil equipamentos isso quebrava de duas formas ao mesmo tempo: o limite CORTAVA
 * em silencio — o operador nao via os que sobraram nem tinha como saber que
 * faltava — e desenhar milhares de checkboxes travava o navegador.
 *
 * A selecao vive por ID, nao pelo objeto. Isso importa porque o host escolhido na
 * pagina 1 continua selecionado depois de uma busca que nao o traz de volta: sem
 * isso, filtrar perderia silenciosamente o que ja tinha sido marcado.
 */
export default function SeletorDeHosts({ empresa, selecionados, aoMudar, altura = '38vh' }) {
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(0);
  const [dados, setDados] = useState({ total: 0, hosts: [] });
  const [carregando, setCarregando] = useState(false);
  const [marcados, setMarcados] = useState([]);

  const debounce = useRef(null);

  const buscar = useCallback(async (termo, off) => {
    if (!empresa) return;
    setCarregando(true);
    try {
      const res = await api.get(`${ROTA}/group/hosts/search`, {
        params: { empresa, q: termo || undefined, limit: POR_PAGINA, offset: off },
      });
      setDados(res.data?.data || { total: 0, hosts: [] });
    } catch {
      setDados({ total: 0, hosts: [] });
    } finally {
      setCarregando(false);
    }
  }, [empresa]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => buscar(busca, pagina * POR_PAGINA), 300);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [busca, pagina, buscar]);

  // Nomes do que está selecionado mas fora da página atual — senão o operador
  // perde de vista o que já escolheu.
  useEffect(() => {
    if (!selecionados.length || !empresa) { setMarcados([]); return; }
    api.get(`${ROTA}/group/hosts/search`, {
      params: { empresa, ids: selecionados.join(',') },
    })
      .then((r) => setMarcados(r.data?.data?.hosts || []))
      .catch(() => setMarcados([]));
  }, [selecionados, empresa]);

  const setSel = useMemo(() => new Set(selecionados), [selecionados]);

  const alternar = (id) => {
    aoMudar(setSel.has(id) ? selecionados.filter((x) => x !== id) : [...selecionados, id]);
  };

  const paginaMarcada =
    dados.hosts.length > 0 && dados.hosts.every((h) => setSel.has(h.id));

  const alternarPagina = () => {
    const ids = dados.hosts.map((h) => h.id);
    if (paginaMarcada) aoMudar(selecionados.filter((x) => !ids.includes(x)));
    else aoMudar([...new Set([...selecionados, ...ids])]);
  };

  const ultimaPagina = Math.max(0, Math.ceil(dados.total / POR_PAGINA) - 1);

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small" placeholder="nome, IP ou vendor" sx={{ width: 250 }}
          value={busca}
          onChange={(e) => { setBusca(e.target.value); setPagina(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
        {carregando && <CircularProgress size={14} />}
        <Chip
          size="small"
          label={`${selecionados.length} selecionado(s)`}
          sx={{
            height: 21,
            bgcolor: selecionados.length ? '#2563eb' : '#e2e8f0',
            color: selecionados.length ? '#fff' : '#64748b',
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Marca ou desmarca só os desta página">
          <span>
            <Button size="small" onClick={alternarPagina} disabled={!dados.hosts.length}>
              {paginaMarcada ? 'desmarcar' : 'marcar'} esta página
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* O que está selecionado e não aparece na página atual. */}
      {!!marcados.length && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          {marcados.slice(0, 12).map((h) => (
            <Chip
              key={h.id} size="small" label={h.host_name}
              onDelete={() => alternar(h.id)}
              sx={{ height: 20, bgcolor: '#eff6ff', color: '#1e3a8a' }}
            />
          ))}
          {marcados.length > 12 && (
            <Chip size="small" label={`+${marcados.length - 12}`}
              sx={{ height: 20, bgcolor: '#e2e8f0', color: '#475569' }} />
          )}
        </Stack>
      )}

      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, height: altura, overflow: 'auto' }}>
        <List dense disablePadding>
          {dados.hosts.map((h) => (
            <ListItemButton key={h.id} onClick={() => alternar(h.id)} dense sx={{ py: 0.2 }}>
              <Checkbox
                edge="start" size="small" tabIndex={-1} disableRipple
                checked={setSel.has(h.id)} sx={{ py: 0.25 }}
              />
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{h.host_name}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {h.ip_address}
                    </Typography>
                    {h.vendor_name && (
                      <Chip size="small" label={h.vendor_name}
                        sx={{ height: 16, fontSize: 10.5, bgcolor: '#f1f5f9', color: '#475569' }} />
                    )}
                  </Stack>
                }
              />
            </ListItemButton>
          ))}
          {!dados.hosts.length && !carregando && (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {busca ? `Nenhum equipamento com “${busca}”.` : 'Esta empresa não tem equipamentos.'}
              </Typography>
            </Box>
          )}
        </List>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {dados.total
            ? `${pagina * POR_PAGINA + 1}–${Math.min((pagina + 1) * POR_PAGINA, dados.total)} de ${dados.total}`
            : '—'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>
          anterior
        </Button>
        <Button size="small" disabled={pagina >= ultimaPagina} onClick={() => setPagina((p) => p + 1)}>
          próxima
        </Button>
      </Stack>
    </Box>
  );
}
